import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';
import Modal from '../../shared/components/ui/Modal';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getAllCompanies,
  getGoogleAdsConfigCheck,
  getGoogleAdsOAuthUrl,
  exchangeGoogleAdsOAuthToken,
  getGoogleAdsStatusByCompany,
  getGoogleAdsAccounts,
  connectGoogleAdsAccount,
  disconnectGoogleAds
} from '../services/api/implementation/impl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const GOOGLE_ADS_CALLBACK_PATH = '/builder/integration/google-ads-companies';

const GoogleAdsCompanies = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [statusLoadingMap, setStatusLoadingMap] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openAccountsModal, setOpenAccountsModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const { collapsed } = useSidebar();

  const code = searchParams.get('code');
  const state = searchParams.get('state');

  const redirectUri =
    typeof window !== 'undefined'
      ? `${window.location.origin}${GOOGLE_ADS_CALLBACK_PATH}`
      : '';

  useEffect(() => {
    mixpanel.track('Google Ads Companies Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const { data: companiesData, isPending: isCompaniesPending } = useQuery({
    queryKey: ['companies'],
    queryFn: getAllCompanies
  });

  const { data: configCheck } = useQuery({
    queryKey: ['googleAdsConfigCheck'],
    queryFn: getGoogleAdsConfigCheck
  });

  const isAppConfigured = configCheck?.configured === true;

  const checkGoogleAdsStatus = useCallback(async (companyId) => {
    setStatusLoadingMap((prev) => ({ ...prev, [companyId]: true }));
    try {
      const data = await getGoogleAdsStatusByCompany(companyId);
      setStatusMap((prev) => ({ ...prev, [companyId]: data }));
    } catch {
      setStatusMap((prev) => ({
        ...prev,
        [companyId]: { configured: false, oauth_complete: false, is_connected: false }
      }));
    } finally {
      setStatusLoadingMap((prev) => ({ ...prev, [companyId]: false }));
    }
  }, []);

  useEffect(() => {
    if (companiesData) {
      setCompanies(companiesData);
      companiesData.forEach((company) => {
        checkGoogleAdsStatus(company.id);
      });
    }
  }, [companiesData, checkGoogleAdsStatus]);

  const { mutate: handleExchangeToken } = useMutation({
    mutationFn: (payload) => exchangeGoogleAdsOAuthToken(payload),
    onSuccess: async (data, variables) => {
      toast.success('Google account authorized successfully');
      const companyId = variables.company_id;
      if (companyId) {
        await checkGoogleAdsStatus(companyId);
        setSelectedCompanyId(companyId);
        await loadAccounts(companyId);
      }
      window.history.replaceState({}, '', window.location.pathname);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Error connecting to Google Ads'
      );
    }
  });

  useEffect(() => {
    if (code && state) {
      const companyId = parseInt(state, 10);
      handleExchangeToken({
        code,
        company_id: companyId,
        redirect_uri: redirectUri
      });

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('code');
      newParams.delete('state');
      newParams.delete('scope');
      newParams.delete('authuser');
      newParams.delete('prompt');
      setSearchParams(newParams);
    }
  }, [code, state]);

  const loadAccounts = async (companyId) => {
    try {
      const data = await getGoogleAdsAccounts(companyId);
      setAccounts(Array.isArray(data) ? data : []);
      setOpenAccountsModal(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to load Google Ads accounts'
      );
    }
  };

  const handleGoogleConnect = async (companyId) => {
    if (!isAppConfigured) {
      toast.error(
        'Google Ads is not configured on the server yet. Add GOOGLE_ADS_* env vars first.'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const oauthData = await getGoogleAdsOAuthUrl(companyId, redirectUri);
      if (oauthData?.url) {
        window.location.href = oauthData.url;
      } else {
        toast.error('Failed to get Google OAuth URL');
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Error starting Google OAuth'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectAccount = async () => {
    if (!selectedCompanyId || !selectedCustomerId) {
      toast.error('Select a Google Ads account');
      return;
    }

    const account = accounts.find((a) => a.customer_id === selectedCustomerId);
    setIsSubmitting(true);
    try {
      await connectGoogleAdsAccount({
        company_id: selectedCompanyId,
        customer_id: selectedCustomerId,
        customer_name: account?.descriptive_name || null,
        manager_customer_id: account?.manager_customer_id ?? null
      });
      toast.success('Google Ads account connected');
      setOpenAccountsModal(false);
      setSelectedCustomerId(null);
      await checkGoogleAdsStatus(selectedCompanyId);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to connect Google Ads account'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisconnect = async (companyId) => {
    setIsSubmitting(true);
    try {
      await disconnectGoogleAds(companyId);
      toast.success('Google Ads disconnected');
      await checkGoogleAdsStatus(companyId);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to disconnect');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCompanyAction = (company) => {
    const status = statusMap[company.id];
    const isLoading = statusLoadingMap[company.id];

    if (isLoading) {
      return (
        <Button isLoading disabled displayType="primary">
          Loading
        </Button>
      );
    }

    if (status?.is_connected) {
      return (
        <div className="flex gap-2 flex-wrap justify-end">
          <Button
            displayType="secondary"
            onClick={() =>
              navigate('/builder/integration/google-ads-companies/lead-sources', {
                state: { cmpId: company.id, companyName: company.company_name }
              })
            }>
            Manage Lead Sources
          </Button>
          <Button displayType="secondary" onClick={() => handleDisconnect(company.id)} disabled={isSubmitting}>
            Disconnect
          </Button>
          <Button displayType="primary" disabled>
            Connected
          </Button>
        </div>
      );
    }

    if (status?.oauth_complete) {
      return (
        <Button
          onClick={async () => {
            setSelectedCompanyId(company.id);
            await loadAccounts(company.id);
          }}
          disabled={isSubmitting}>
          Select Account
        </Button>
      );
    }

    return (
      <Button onClick={() => handleGoogleConnect(company.id)} disabled={isSubmitting || !isAppConfigured}>
        Connect Google
      </Button>
    );
  };

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow={true}
          onBack={() => navigate('/builder/integration')}
          data={{ name: 'Integrations > Google Ads' }}
        />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="mt-[4.5rem] w-full h-full bg-gray-100 p-6">
          {!isAppConfigured && (
            <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-semibold mb-1">Server configuration required</p>
              <p>
                Add <code>GOOGLE_ADS_CLIENT_ID</code>, <code>GOOGLE_ADS_CLIENT_SECRET</code>,{' '}
                <code>GOOGLE_ADS_DEVELOPER_TOKEN</code>, and <code>GOOGLE_ADS_REDIRECT_URI</code>{' '}
                to the backend <code>.env</code>. OAuth redirect for this page:{' '}
                <code>{redirectUri}</code>
              </p>
            </div>
          )}

          {isCompaniesPending ? (
            <div className="flex min-h-[40vh] w-full items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#346C75] border-t-transparent" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {companies.map((company) => {
                const status = statusMap[company.id];
                return (
                  <div key={company.id} className="bg-white shadow-md p-6 rounded-lg flex flex-col">
                    {company.company_logo && (
                      <img
                        className="border h-[6rem] object-contain mb-2"
                        src={company.company_logo}
                        alt="Company Logo"
                      />
                    )}
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-bold text-lg">{company.company_name}</h3>
                      {status?.is_connected && (
                        <div className="text-[#418b7a] flex gap-[2px] justify-center items-center text-sm font-medium">
                          <span className="border bg-[#418b7a] rounded-full p-[0.20rem] text-[#418b7a] mt-[2px]"></span>
                          <p>Live</p>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2 text-justify flex-grow">
                      Connect Google Ads to pull lead form and website bot campaign leads into
                      prelead lists automatically.
                    </p>
                    {status?.customer_name && (
                      <p className="text-xs text-gray-500 mb-4">
                        Account: {status.customer_name} ({status.customer_id})
                      </p>
                    )}
                    <div className="w-full flex justify-end mt-auto">{getCompanyAction(company)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {openAccountsModal && (
        <Modal
          isDirty={true}
          width={60}
          name="Select Google Ads Account"
          onClose={() => {
            setOpenAccountsModal(false);
            setSelectedCustomerId(null);
            setAccounts([]);
          }}>
          <div className="flex flex-col">
            {accounts.length === 0 ? (
              <p className="text-sm text-gray-600 py-4">No accessible Google Ads accounts found.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {accounts.map((account) => (
                  <div
                    key={account.customer_id}
                    onClick={() => setSelectedCustomerId(account.customer_id)}
                    className={`cursor-pointer bg-white shadow-md p-4 rounded-lg border ${
                      selectedCustomerId === account.customer_id
                        ? 'border-2 border-green-500'
                        : 'border-gray-200'
                    }`}>
                    <h3 className="font-semibold">
                      {account.descriptive_name || `Account ${account.customer_id}`}
                    </h3>
                    <p className="text-sm text-gray-500">ID: {account.customer_id}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button
                type="button"
                displayType="secondary"
                onClick={() => {
                  setOpenAccountsModal(false);
                  setSelectedCustomerId(null);
                }}>
                Cancel
              </Button>
              <Button type="button" disabled={isSubmitting || !selectedCustomerId} onClick={handleSelectAccount}>
                Connect Account
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default GoogleAdsCompanies;
